export type AstralTemporalInputKind = 'date' | 'datetime-local' | 'time';

export interface AstralDateInputParts {
  year: number;
  month: number;
  day: number;
}

const COMPACT_DATE_PATTERN = /^\d{8}$/;
const DATE_PARTS_PATTERN = /^(\d+)-(\d+)-(\d+)$/;
const COMPACT_DATE_TIME_PATTERN = /^(\d{8})(\d{2})(\d{2})$/;
const DATE_TIME_PATTERN = /^(.*)[T ](\d{2}):(\d{2})$/;
const TIME_PATTERN = /^(?:\d{4}|\d{2}:\d{2})$/;

/**
 * Converts supported compact and canonical temporal input to the exact value
 * format expected by the corresponding native input. Numeric date parts are
 * deliberately clamped instead of delegated to JavaScript Date parsing.
 */
export function normalizeAstralTemporalInput(value: string, kind: AstralTemporalInputKind): string | null {
  const input = value.trim();

  if (kind === 'time') {
    if (!TIME_PATTERN.test(input)) {
      return null;
    }

    const digits = input.replace(':', '');
    const hour = Number(digits.slice(0, 2));
    const minute = Number(digits.slice(2, 4));
    return isValidTime(hour, minute) ? `${digits.slice(0, 2)}:${digits.slice(2, 4)}` : null;
  }

  if (kind === 'date') {
    const dateParts = parseAstralDateInput(input);
    return dateParts ? formatDateParts(dateParts) : null;
  }

  const dateTimeParts = input.match(COMPACT_DATE_TIME_PATTERN) ?? input.match(DATE_TIME_PATTERN);
  if (!dateTimeParts) {
    return null;
  }

  const dateParts = parseAstralDateInput(dateTimeParts[1]);
  if (!dateParts) {
    return null;
  }

  const normalizedDate = formatDateParts(dateParts);
  const hourText = dateTimeParts[2];
  const minuteText = dateTimeParts[3];
  const hour = Number(hourText);
  const minute = Number(minuteText);
  return isValidTime(hour, minute) ? `${normalizedDate}T${hourText}:${minuteText}` : null;
}

export function parseAstralDateInput(value: string): AstralDateInputParts | null {
  const input = value.trim();
  const compact = COMPACT_DATE_PATTERN.test(input);
  const match = compact ? null : input.match(DATE_PARTS_PATTERN);
  if (!compact && !match) {
    return null;
  }

  const year = clampInteger(compact ? input.slice(0, 4) : match[1], 1, 9999);
  const month = clampInteger(compact ? input.slice(4, 6) : match[2], 1, 12);
  const day = clampInteger(compact ? input.slice(6, 8) : match[3], 1, daysInMonth(year, month));
  return { year, month, day };
}

export function compactAstralTemporalInputLength(kind: AstralTemporalInputKind): number {
  switch (kind) {
    case 'date':
      return 8;
    case 'datetime-local':
      return 12;
    case 'time':
      return 4;
  }
}

function daysInMonth(year: number, month: number): number {
  const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return daysInMonth[month - 1];
}

function clampInteger(value: string, minimum: number, maximum: number): number {
  const parsed = Number(value);
  return Math.max(minimum, Math.min(maximum, Number.isFinite(parsed) ? parsed : minimum));
}

function formatDateParts(parts: AstralDateInputParts): string {
  return `${parts.year.toString().padStart(4, '0')}-${parts.month.toString().padStart(2, '0')}-${parts.day
    .toString()
    .padStart(2, '0')}`;
}

function isValidTime(hour: number, minute: number): boolean {
  return Number.isInteger(hour) && hour >= 0 && hour <= 23 && Number.isInteger(minute) && minute >= 0 && minute <= 59;
}

function isLeapYear(year: number): boolean {
  return year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0);
}
